import { DocumentType } from 'src/services/documents/types';
import { RuleBuilderJsonEntry } from 'src/services/loans/types';

export const extractBracketedValues = (str: string) => {
  const results = [];
  let inside = false;
  let buffer = '';

  for (const char of str) {
    if (char === '[' && !inside) {
      inside = true;
      buffer = '';
    } else if (char === ']' && inside) {
      results.push(buffer);
      inside = false;
    } else if (inside) {
      buffer += char;
    }
  }

  return results.filter((item) => item !== '');
};

/**
 * Walk through an expression, invoking a callback for each non-hash
 * bracketed segment that has a corresponding ruleBuilderJson entry.
 * Returns the rebuilt expression string.
 */
function walkExpressionBrackets(
  expression: string,
  ruleBuilderJson: RuleBuilderJsonEntry[],
  onEntry: (bracketContent: string, entry: RuleBuilderJsonEntry) => string,
): string {
  let result = '';
  let jsonIndex = 0;
  let i = 0;

  while (i < expression.length) {
    if (expression[i] === '[') {
      const closeBracket = expression.indexOf(']', i);
      if (closeBracket === -1) {
        result += expression[i];
        i++;
        continue;
      }

      const bracketContent = expression.substring(i + 1, closeBracket);

      if (bracketContent.startsWith('#')) {
        result += `[${bracketContent}]`;
      } else if (jsonIndex < ruleBuilderJson.length) {
        result += onEntry(bracketContent, ruleBuilderJson[jsonIndex]);
        jsonIndex++;
      } else {
        result += `[${bracketContent}]`;
      }

      i = closeBracket + 1;
    } else {
      result += expression[i];
      i++;
    }
  }

  return result;
}

/**
 * Append extra bracketed-content parts (table column key, definition, etc.)
 * that appear after the doc-type and optional field display-name segments.
 */
function appendExtraParts(
  bracketContent: string,
  hasField: boolean,
  base: string,
): string {
  const parts = bracketContent.split('.');
  const displayParts = hasField ? 2 : 1;
  if (parts.length > displayParts) {
    return base + '.' + parts.slice(displayParts).join('.');
  }
  return base;
}

/**
 * Build ruleBuilderJson from expression by matching display names
 * against the current document types data.
 */
export function buildRuleBuilderJson(
  expression: string,
  documentTypes: DocumentType[],
): RuleBuilderJsonEntry[] {
  if (!documentTypes || documentTypes.length === 0) return [];

  const bracketedValues = extractBracketedValues(expression);
  const entries: RuleBuilderJsonEntry[] = [];

  for (const value of bracketedValues) {
    if (value.startsWith('#')) continue;

    const parts = value.split('.');
    const docTypeParts = parts[0].split(':');
    const docTypeDisplayName = docTypeParts[0];
    const internalVersion = docTypeParts[1];

    const docType = documentTypes.find(
      (dt) => dt.displayName === docTypeDisplayName,
    );
    if (!docType) continue;

    const entry: RuleBuilderJsonEntry = {
      documentTypeName: docType.name,
      documentTypeDisplayName: docType.displayName,
    };
    if (internalVersion) {
      entry.internalVersion = internalVersion;
    }

    if (parts.length >= 2) {
      const fieldDisplayName = parts[1];
      const field = docType.fields?.find(
        (f) => f.displayName === fieldDisplayName,
      );
      if (field) {
        entry.fieldTypeName = field.name;
        entry.fieldTypeDisplayName = field.displayName;
      }
    }

    entries.push(entry);
  }

  return entries;
}

/**
 * Rebuild expression with latest display names using the stored
 * system names in ruleBuilderJson. This handles cases where
 * document type or field type display names have been renamed.
 *
 * Returns the updated expression and an updated ruleBuilderJson
 * with current display names.
 */
export function rebuildExpressionWithLatestDisplayNames(
  expression: string,
  ruleBuilderJson: RuleBuilderJsonEntry[],
  documentTypes: DocumentType[],
): { expression: string; updatedJson: RuleBuilderJsonEntry[] } {
  if (
    !ruleBuilderJson ||
    ruleBuilderJson.length === 0 ||
    !documentTypes ||
    documentTypes.length === 0
  ) {
    return { expression, updatedJson: ruleBuilderJson || [] };
  }

  const updatedJson: RuleBuilderJsonEntry[] = [];

  const result = walkExpressionBrackets(
    expression,
    ruleBuilderJson,
    (bracketContent, entry) => {
      const docType = documentTypes.find(
        (dt) => dt.name === entry.documentTypeName,
      );

      if (docType) {
        let newDisplayValue = docType.displayName;
        if (entry.internalVersion) {
          newDisplayValue += `:${entry.internalVersion}`;
        }
        const newEntry: RuleBuilderJsonEntry = {
          documentTypeName: docType.name,
          documentTypeDisplayName: docType.displayName,
        };
        if (entry.internalVersion) {
          newEntry.internalVersion = entry.internalVersion;
        }

        if (entry.fieldTypeName) {
          const field = docType.fields?.find(
            (f) => f.name === entry.fieldTypeName,
          );
          if (field) {
            newDisplayValue += `.${field.displayName}`;
            newEntry.fieldTypeName = field.name;
            newEntry.fieldTypeDisplayName = field.displayName;
          }
        }

        newDisplayValue = appendExtraParts(
          bracketContent,
          !!entry.fieldTypeName,
          newDisplayValue,
        );

        updatedJson.push(newEntry);
        return `[${newDisplayValue}]`;
      } else {
        updatedJson.push(entry);
        return `[${bracketContent}]`;
      }
    },
  );

  return { expression: result, updatedJson };
}

/**
 * Build a name-based expression by replacing display names in brackets
 * with system names from the ruleBuilderJson.
 */
export function buildNameExpression(
  expression: string,
  ruleBuilderJson: RuleBuilderJsonEntry[],
): string {
  if (!ruleBuilderJson || ruleBuilderJson.length === 0) return expression;

  return walkExpressionBrackets(
    expression,
    ruleBuilderJson,
    (bracketContent, entry) => {
      let nameValue = entry.documentTypeName;
      if (entry.internalVersion) {
        nameValue += `:${entry.internalVersion}`;
      }

      if (entry.fieldTypeName) {
        nameValue = `${entry.documentTypeName}${entry.internalVersion ? ':' + entry.internalVersion : ''}.${entry.fieldTypeName}`;
      }

      nameValue = appendExtraParts(
        bracketContent,
        !!entry.fieldTypeName,
        nameValue,
      );

      return `[${nameValue}]`;
    },
  );
}
